import type { PhraseRef, PlayerView, ViewEvent } from '@il-colpo/engine';
import type { LooseT } from './loose.ts';
import { phraseText } from './phraseText.ts';

export interface EventLine {
  id: number;
  atMs: number;
  /** Who said it, for chat lines. */
  who: string | null;
  text: string;
  tone: 'chat' | 'notice' | 'alarm' | 'good' | 'bad' | 'plain';
}

export interface EventTextContext {
  g: LooseT;
  phrases: LooseT;
  players: PlayerView['players'];
  /** Language-neutral name of the viewing player, used for "you" in a few lines. */
  me: string;
}

const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

/** One readable line for an event the player may see, or null for the ones that are only noise. */
export function eventLine(ev: ViewEvent, c: EventTextContext): EventLine | null {
  const d = ev.data;
  const name = (id: unknown): string => c.players.find((p) => p.id === id)?.name ?? str(id);
  const make = (text: string, tone: EventLine['tone'], who: string | null = null): EventLine => ({
    id: ev.id,
    atMs: ev.atMs,
    who,
    text,
    tone,
  });
  const line = (
    key: string,
    values: Record<string, unknown> = {},
    tone: EventLine['tone'] = 'plain',
  ) => make(c.g(`events.${key}`, values), tone);

  switch (ev.type) {
    case 'chat': {
      const phrase = d['phrase'] as PhraseRef;
      return make(phraseText({ t: c.phrases, players: c.players }, phrase), 'chat', name(d['by']));
    }
    case 'ping':
      return line(`ping.${str(d['kind'])}`, { who: name(d['by']) });
    case 'notice':
      return line(`notice.${str(d['kind'])}`, {}, 'notice');
    case 'clue.glitch':
      return line('glitch', {}, 'notice');
    case 'alarm': {
      const delta = Number(d['delta'] ?? 0);
      return delta > 0
        ? line(
            'alarm',
            { delta: (delta / 10).toFixed(1), total: Math.floor(Number(d['alarm']) / 10) },
            'alarm',
          )
        : null;
    }
    case 'alarm.policeAlerted':
      return line('police', {}, 'alarm');
    case 'alarm.optionalClosed':
      return line('alcovesClosed', {}, 'alarm');
    case 'puzzle.solved':
      return line('solved', { puzzle: c.g(`puzzles.${str(d['type'])}.name`) }, 'good');
    case 'room.cleared':
      return line('roomCleared', { room: c.g(`rooms.${str(d['room'])}`) }, 'good');
    case 'stop.room':
      return line('entering', { room: c.g(`rooms.${str(d['room'])}`) });
    case 'stop.corridor':
      return line('corridor', {});
    case 'stop.skipped':
      return line('skipped', { room: c.g(`rooms.${str(d['room'])}`) });
    case 'stop.rope':
      return line('rope', {});
    case 'corridor.sighted':
      return line('sighted', {}, 'alarm');
    case 'plan.proposed':
      return line('planProposed', {});
    case 'plan.voteResult':
      return line('planVote', { yes: str(d['yes']), no: str(d['no']) });
    case 'plan.approved':
      return line('planApproved', {}, 'good');
    case 'plan.rejected':
      return line('planRejected', {}, 'bad');
    case 'plan.tie':
      return line('planTie', {});
    case 'exec.started':
      return line('started', {});
    case 'escape.started':
      return line('escape', {});
    case 'escape.radio':
      return line('radio', { side: c.phrases(`enum.${str(d['side'])}`) }, 'notice');
    case 'escape.reinforcement':
      return line('reinforcement', {}, 'alarm');
    case 'radio.bulletin':
      return line('bulletin', { kind: c.g(`events.notice.${str(d['kind'])}`) }, 'notice');
    case 'gear.used':
      return line('gearUsed', { gear: c.g(`gear.${str(d['gear'])}.name`) });
    case 'gear.disguise':
      return line('disguise', {}, 'good');
    case 'power.loop':
      return line('loop', {});
    case 'power.planB':
      return line('planB', {}, 'good');
    case 'tunnel':
      return line('tunnel', {}, 'notice');
    case 'heist.failed':
      return line(`failed.${str(d['reason'])}`, {}, 'bad');
    case 'heist.completed':
      return line('completed', {}, 'good');
    case 'accusation.result':
      return d['expelled']
        ? line('expelled', { who: name(d['expelled']) }, 'bad')
        : line('nobodyOut', {});
    case 'split.proposed':
      return line('splitProposed', { who: name(d['by']) });
    case 'split.result':
      return line(d['approved'] ? 'splitApproved' : 'splitRejected', {});
    case 'split.equal':
      return line('splitEqual', {});
    default:
      return null;
  }
}
